import React, { useEffect } from 'react';
import {
    TextField,
} from "@mui/material";
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { AccountItem, AccountItemList, useGetAccountQuery, useGetTopLevelAccountsQuery, useGetAccountLookupQuery } from "../../../services/grace";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

//redux
import { useAppSelector } from "../../../util/hooks";

//loadash
import { uniqBy } from 'lodash';

export interface AccountPickerValue {
    id: string;
    label: string;
}

export type AccountPickerValueList = Array<AccountPickerValue>

export interface AccountPickerProps {
    className?: string;
    label: string;
    parentValue?: string;
    value: string;
    error?: boolean;
    helperText?: React.ReactNode;
    required?: boolean;
    disabled?: boolean;
    onChange: (value: string) => void;
    name: "level1" | "level2" | "level3";
    type: "basic" | "checkout" | "address" | "container" | "file" | "tape";
}

export default function AccountPicker(props: AccountPickerProps): JSX.Element {
    const {
        className,
        label,
        parentValue,
        value,
        error,
        helperText,
        required = false,
        disabled = false,
        onChange,
        name,
        type
    } = props;

    const searchBasicFormText = useAppSelector((state) => state?.search.searchBasicFormText);
    const searchAddressFormText = useAppSelector((state) => state?.search.searchAddressFormText);
    const searchCheckoutFormText = useAppSelector((state) => state?.search.searchCheckoutFormText);
    const searchFileFormText = useAppSelector((state) => state?.search.searchFileFormText);
    const searchTapeFormText = useAppSelector((state) => state?.search.searchTapeFormText);
    const searchContainerFormText = useAppSelector((state) => state?.search.searchContainerFormText);

    const filterAccountTypeSearchText = (
      type: "basic" | "checkout" | "address" | "container" | "file" | "tape"
    ): string | null => {
      let result;
      switch (type) {
        case "basic":
          result = searchBasicFormText;
          break;
        case "checkout":
          result = searchCheckoutFormText;
          break;
        case "address":
          result = searchAddressFormText;
          break;
        case "container":
          result = searchContainerFormText;
          break;
        case "file":
          result = searchFileFormText;
          break;
        case "tape":
          result = searchTapeFormText;
          break;
      }
      return result;
    };

    // Top level data errors are handled in App.tsx
    const {
        data: topLevelData,
    } = useGetTopLevelAccountsQuery(null);

    const {
        data: parentData,
        error: errorParentData,
        isError: isErrorParentData
    } = useGetAccountQuery(
        parentValue === undefined
            ? ''
            : parentValue, { skip: parentValue === undefined }
    );

    const {
        data: searchData,
    } = useGetAccountLookupQuery(filterAccountTypeSearchText(type), {skip:filterAccountTypeSearchText(type) == null});

    const alertDispatch = useAlertDispatch();

    useEffect(() => {
        if (isErrorParentData) {
            const err = errorParentData as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load account data",
                    }
                });
            }
        }
    }, [errorParentData, isErrorParentData, alertDispatch])

    const formatOptions = (data: AccountItemList | undefined , search=false) => {
        const result: AccountPickerValueList = [];
        if (data !== undefined && !search) {
            data.forEach(item => {
                const labelText = item.name === undefined ? item.code : `(${item.code}) ${item.name}`;
                result.push({ id: item.id, label: labelText });
            });
        } else {
            data?.forEach((item:any) => {
                if(item[name]){
                    const labelText = item[name].name === undefined ? item[name].code : `(${item[name].code}) ${item[name].name}`;
                    result.push({ id: item[name].id, label: labelText });
                }
            });
        }
        return uniqBy(result, 'id');
    }

    const formatChildren = (data: AccountItem | undefined) => {
        return data === undefined ? [] : formatOptions(data.children);
    }

    const options: AccountPickerValueList =
        (parentValue) ?  formatChildren(parentData) :
             (filterAccountTypeSearchText(type) === null ? formatOptions(topLevelData) : formatOptions(searchData, true))

    // eslint-disable-next-line @typescript-eslint/ban-types
    const handleChange = (event: React.ChangeEvent<{}>, newValue: AccountPickerValue | null) => {
        onChange(newValue === null ? '' : newValue.id);
    };

    const getOptionLabel = (option: AccountPickerValue) => option.label;

    const getOptionSelected = (option: AccountPickerValue, value: AccountPickerValue) => option.id === value.id;

    const renderInput = (params: AutocompleteRenderInputParams) => {
        return (
            <TextField
                {...params}
                required={required}
                error={error}
                helperText={helperText}
                label={label}
                variant="outlined"
                size="small"
            />
        );
    }

    const currentValue = options.find(v => v.id === value);
    return (
        <Autocomplete
            id={name}
            className={className}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={getOptionSelected}
            value={currentValue === undefined ? null : currentValue}
            disabled={filterAccountTypeSearchText(type) === null && disabled || options.length < 1}
            renderInput={renderInput}
            onChange={handleChange}
        />
    );
}
